import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Bed, Bath, Car, Maximize, ChevronLeft, ChevronRight, Heart, Eye } from 'lucide-react';
import { companyInfo } from '../data/mock';

const PropertyCard = ({ property, onViewDetails }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImage((prev) => (prev + 1) % property.images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImage((prev) => (prev - 1 + property.images.length) % property.images.length);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  };

  return (
    <Card
      data-testid={`property-card-${property.id}`}
      className="group overflow-hidden bg-[#1A1A1A] rounded-none shadow-none hover:shadow-xl hover:shadow-[#D4A520]/5 transition-all duration-500 border border-[#D4A520]/10 hover:border-[#D4A520]/30"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={property.images[currentImage]}
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {/* Navigation arrows */}
        {property.images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              data-testid={`property-${property.id}-prev-img`}
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 backdrop-blur-sm border border-[#D4A520]/30 flex items-center justify-center transition-all duration-300 hover:bg-[#D4A520] hover:border-[#D4A520] ${
                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={nextImage}
              data-testid={`property-${property.id}-next-img`}
              className={`absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 backdrop-blur-sm border border-[#D4A520]/30 flex items-center justify-center transition-all duration-300 hover:bg-[#D4A520] hover:border-[#D4A520] ${
                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </>
        )}

        {/* Image indicators */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {property.images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentImage(idx); }}
              className={`w-2 h-2 transition-all duration-300 ${
                idx === currentImage
                  ? 'bg-[#D4A520] w-6'
                  : 'bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className="bg-[#D4A520] text-black font-semibold px-3 py-1 rounded-none text-xs uppercase tracking-wider">
            {property.type}
          </Badge>
          {property.featured && (
            <Badge className="bg-white/10 backdrop-blur-sm text-white border border-white/20 font-medium px-3 py-1 rounded-none text-xs uppercase tracking-wider">
              Destacado
            </Badge>
          )}
        </div>

        {/* Like button */}
        <button
          onClick={(e) => { e.stopPropagation(); setIsLiked(!isLiked); }}
          data-testid={`property-${property.id}-like`}
          className="absolute top-3 right-3 w-10 h-10 bg-black/40 backdrop-blur-sm border border-white/10 flex items-center justify-center transition-all duration-300 hover:border-[#D4A520]/50"
        >
          <Heart
            className={`w-5 h-5 transition-colors ${
              isLiked ? 'text-[#D4A520] fill-[#D4A520]' : 'text-white/70'
            }`}
          />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Price */}
        <div className="mb-3">
          <span className="text-2xl font-bold text-[#D4A520]">
            {property.currency} {formatPrice(property.price)}
          </span>
          {property.priceDollar && (
            <span className="text-sm text-white/40 ml-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              (USD {formatPrice(property.priceDollar)})
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-white mb-2 line-clamp-1 group-hover:text-[#D4A520] transition-colors">
          {property.title}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-white/40 mb-5">
          <MapPin className="w-4 h-4 text-[#D4A520]" />
          <span className="text-sm line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{property.location}</span>
        </div>

        {/* Features */}
        <div className="flex items-center gap-5 pb-5 border-b border-[#D4A520]/10">
          <div className="flex items-center gap-1.5 text-white/50">
            <Bed className="w-4 h-4 text-[#D4A520]/70" />
            <span className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{property.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/50">
            <Bath className="w-4 h-4 text-[#D4A520]/70" />
            <span className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{property.bathrooms}</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/50">
            <Car className="w-4 h-4 text-[#D4A520]/70" />
            <span className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{property.parking}</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/50">
            <Maximize className="w-4 h-4 text-[#D4A520]/70" />
            <span className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{property.area} m²</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-5">
          <Button
            onClick={() => onViewDetails(property)}
            variant="outline"
            data-testid={`property-${property.id}-view-details`}
            className="flex-1 border border-[#D4A520]/30 text-[#D4A520] bg-transparent hover:bg-[#D4A520] hover:text-black font-semibold rounded-none transition-all duration-300 uppercase text-xs tracking-wider"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <Eye className="w-4 h-4 mr-2" />
            Ver Detalles
          </Button>
          <a
            href={`https://wa.me/${companyInfo.whatsapp}?text=Hola, me interesa la propiedad: ${property.title}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button
              data-testid={`property-${property.id}-consult`}
              className="w-full bg-[#D4A520] hover:bg-[#FFD700] text-black font-semibold rounded-none transition-all duration-300 uppercase text-xs tracking-wider hover:-translate-y-[2px]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Consultar
            </Button>
          </a>
        </div>
      </div>
    </Card>
  );
};

export default PropertyCard;
